import React from "react";
import { Paper, Title, Text, List, ThemeIcon, Stack } from "@mantine/core";
import { IconCheck, IconCurrencyBitcoin, IconBolt } from "@tabler/icons-react";

const AddressExplainer: React.FC = () => (
  <Stack gap="md" mt="xl">
    <Paper
      p="lg"
      radius="md"
      style={{
        backgroundColor: "rgba(0, 0, 0, 0.3)",
        border: "1px solid rgba(255, 255, 255, 0.08)",
      }}
    >
      <Title order={3} c="white" mb="sm">
        Bitcoin Address Formats Explained
      </Title>
      <Text c="dimmed" size="sm" mb="md">
        Bitcoin has evolved through several address formats over the years. Each
        format offers different trade-offs between compatibility, fees, and
        features.
      </Text>
      <List
        spacing="xs"
        size="sm"
        c="dimmed"
        icon={
          <ThemeIcon color="orange" size={20} radius="xl" variant="light">
            <IconCurrencyBitcoin size={12} />
          </ThemeIcon>
        }
      >
        <List.Item>
          <Text span fw={600} c="white">
            Legacy P2PKH (1...)
          </Text>{" "}
          — the original format from 2009. Highest fees but supported
          everywhere.
        </List.Item>
        <List.Item>
          <Text span fw={600} c="white">
            P2SH (3...)
          </Text>{" "}
          — introduced for multi-sig and wrapped SegWit. Moderate fees.
        </List.Item>
        <List.Item>
          <Text span fw={600} c="white">
            Native SegWit (bc1q...)
          </Text>{" "}
          — the most common modern format. ~30-40% lower fees than legacy.
        </List.Item>
        <List.Item>
          <Text span fw={600} c="white">
            Taproot (bc1p...)
          </Text>{" "}
          — the newest format (2021). Best privacy, lowest fees, smart contract
          support.
        </List.Item>
      </List>
    </Paper>

    <Paper
      p="lg"
      radius="md"
      style={{
        backgroundColor: "rgba(0, 0, 0, 0.3)",
        border: "1px solid rgba(255, 255, 255, 0.08)",
      }}
    >
      <Title order={3} c="white" mb="sm">
        Lightning Network Formats
      </Title>
      <List
        spacing="xs"
        size="sm"
        c="dimmed"
        icon={
          <ThemeIcon color="yellow" size={20} radius="xl" variant="light">
            <IconBolt size={12} />
          </ThemeIcon>
        }
      >
        <List.Item>
          <Text span fw={600} c="white">
            Lightning Address (user@domain)
          </Text>{" "}
          — human-readable addresses for receiving Lightning payments. Uses the
          LNURL-pay protocol under the hood.
        </List.Item>
        <List.Item>
          <Text span fw={600} c="white">
            BOLT11 Invoice (lnbc...)
          </Text>{" "}
          — one-time payment requests encoding amount, destination, and expiry
          details.
        </List.Item>
        <List.Item>
          <Text span fw={600} c="white">
            LNURL (lnurl1...)
          </Text>{" "}
          — encoded URLs enabling interactive payment flows, withdrawals, and
          authentication.
        </List.Item>
      </List>
    </Paper>

    <Paper
      p="lg"
      radius="md"
      style={{
        backgroundColor: "rgba(0, 0, 0, 0.3)",
        border: "1px solid rgba(255, 255, 255, 0.08)",
      }}
    >
      <Title order={3} c="white" mb="sm">
        Common Non-Bitcoin Addresses
      </Title>
      <Text c="dimmed" size="sm" mb="md">
        It's easy to confuse addresses from different blockchains. This tool
        detects the most common formats so you don't accidentally send Bitcoin
        to the wrong network.
      </Text>
      <List
        spacing="xs"
        size="sm"
        c="dimmed"
        icon={
          <ThemeIcon color="red" size={20} radius="xl" variant="light">
            <IconCheck size={12} />
          </ThemeIcon>
        }
      >
        <List.Item>
          <Text span fw={600} c="white">
            Ethereum (0x...)
          </Text>{" "}
          — also used by Polygon, Arbitrum, Optimism, BSC, and other
          EVM-compatible chains.
        </List.Item>
        <List.Item>
          <Text span fw={600} c="white">
            Solana
          </Text>{" "}
          — base58 encoded, 32-44 characters. Used for SOL and SPL tokens.
        </List.Item>
        <List.Item>
          <Text span fw={600} c="white">
            TRON (T...)
          </Text>{" "}
          — commonly used for USDT-TRC20 transfers.
        </List.Item>
        <List.Item>
          <Text span fw={600} c="white">
            Others
          </Text>{" "}
          — Litecoin, Dogecoin, XRP, Monero, Cardano, Cosmos, and Bitcoin Cash
          are also detected.
        </List.Item>
      </List>
    </Paper>
  </Stack>
);

export default AddressExplainer;
